import { TranslocoPipe } from '@jsverse/transloco';
import { Component, ElementRef, OnDestroy, afterNextRender, effect, signal, viewChild } from '@angular/core';
import { RevealDirective } from '../../../shared/animations/reveal.directive';
import { MatchdayCardComponent } from '../../trainings/components/matchday-card.component';
import { DEMO_TRAINING } from '../domain/landing-demo-data';
import { LandingLiveBoardComponent } from './landing-live-board.component';

const STACK_QUERY = '(max-width: 900px)';

@Component({
  selector: 'app-landing-hero',
  standalone: true,
  imports: [TranslocoPipe, RevealDirective, LandingLiveBoardComponent, MatchdayCardComponent],
  template: `
    <section #scene class="hero-scene">
      @if (stacked()) {
        <!-- One column has no room for a scroll-driven presentation: swapping headline, copy and
             panel in place made the page jump as the two slides differ in height. Both stories are
             simply stacked instead and reveal themselves as the user scrolls past them. -->
        <div class="hero-stack">
          <article class="hero-stack-item" appReveal appRevealMargin="0px 0px -25% 0px">
            <h1 class="hero-stack-title">{{ 'landing.playTrackImprove' | transloco }}</h1>
            <p class="hero-stack-message">{{ 'landing.scoreOfflineReplayEveryRallyAndTurnMatchesInto' | transloco }}</p>
            <div class="hero-stack-panel hero-stack-panel--board">
              <app-landing-live-board />
            </div>
          </article>
          <article class="hero-stack-item" appReveal appRevealMargin="0px 0px -25% 0px">
            <h2 class="hero-stack-title">{{ 'landing.trainingManagementForYourCommunity' | transloco }}</h2>
            <p class="hero-stack-message">{{ 'landing.planSessionsFillCourtsRunAttendanceAndKeepYour' | transloco }}</p>
            <app-matchday-card
              class="hero-stack-panel"
              [training]="demoTraining"
              [titleOverride]="'landing.clubTraining' | transloco"
              viewer="preview"
              [interactive]="false"
            />
          </article>
        </div>
      } @else {
        <div
          #hero
          appReveal
          class="hero"
          (mouseenter)="setInteractionPaused(true)"
          (mouseleave)="setInteractionPaused(false)"
          (focusin)="setInteractionPaused(true)"
          (focusout)="onFocusOut($event)"
        >
          <div class="hero-copy">
            <!-- Both headlines stay in the DOM and share one grid cell: the stage is then always as
                 tall as the longer of the two, so the copy below never jumps when the slide flips.
                 The inactive one is inert + aria-hidden so only the visible headline is exposed. -->
            <div class="hero-title-stage">
              <h1 class="hero-title" [class.is-active]="activeSlide() === 0" [attr.inert]="activeSlide() === 0 ? null : ''" [attr.aria-hidden]="activeSlide() === 0 ? null : 'true'">
                {{ 'landing.playTrackImprove' | transloco }}
              </h1>
              <h1 class="hero-title" [class.is-active]="activeSlide() === 1" [attr.inert]="activeSlide() === 1 ? null : ''" [attr.aria-hidden]="activeSlide() === 1 ? null : 'true'">
                {{ 'landing.trainingManagementForYourCommunity' | transloco }}
              </h1>
            </div>
            <div class="hero-message-stage">
              @if (activeSlide() === 0) {
                <div class="hero-message" animate.enter="hero-message-enter" animate.leave="hero-message-leave">
                  <p>{{ 'landing.scoreOfflineReplayEveryRallyAndTurnMatchesInto' | transloco }}</p>
                </div>
              } @else {
                <div class="hero-message" animate.enter="hero-message-enter" animate.leave="hero-message-leave">
                  <p>{{ 'landing.planSessionsFillCourtsRunAttendanceAndKeepYour' | transloco }}</p>
                </div>
              }
            </div>
          </div>
          <div class="hero-visual">
            @if (activeSlide() === 0) {
              <div class="hero-panel hero-panel--board" animate.enter="hero-panel-enter" animate.leave="hero-panel-leave">
                <app-landing-live-board />
              </div>
            } @else {
              <app-matchday-card
                class="hero-panel"
                animate.enter="hero-panel-enter"
                animate.leave="hero-panel-leave"
                [training]="demoTraining"
                [titleOverride]="'landing.clubTraining' | transloco"
                viewer="preview"
                [interactive]="false"
              />
            }
          </div>
        </div>
      }
    </section>
  `,
  styles: [`
    .hero-scene {
      position: relative;
      width: 100%;
    }

    .hero {
      align-items: center;
      display: grid;
      gap: clamp(2rem, 5vw, 4rem);
      grid-template-columns: minmax(0, 1.0fr) minmax(0, 1.2fr);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    /* appReveal adds .is-in when the hero reaches the viewport. These children then settle in as a
       short sequence, so the top of the page reads as one composition instead of five pop-ins.
       Animations, not transitions: the hero is above the fold, so .is-in lands in the same frame as
       the first paint and a transition would have nothing to interpolate from. */
    .hero-title-stage,
    .hero-message-stage,
    .hero-visual { opacity: 0; }

    .hero.is-in .hero-title-stage,
    .hero.is-in .hero-message-stage,
    .hero.is-in .hero-visual {
      animation: fade-in-up var(--motion-slow) var(--ease-out) both;
    }

    .hero.is-in .hero-title-stage { animation-delay: 40ms; }
    .hero.is-in .hero-message-stage { animation-delay: 110ms; }
    .hero.is-in .hero-visual { animation-delay: 150ms; }

    .hero-copy {
      display: grid;
      gap: var(--space-5);
      justify-items: start;
    }

    .hero-title-stage {
      display: grid;
      width: 100%;
    }

    .hero-title {
      filter: blur(8px);
      grid-area: 1 / 1;
      opacity: 0;
      transform: translateY(1.1rem) scale(0.985);
      transform-origin: 0 50%;
      transition:
        filter var(--motion-medium) var(--ease-out),
        opacity var(--motion-medium) var(--ease-out),
        transform var(--motion-slow) var(--ease-spring);
    }

    .hero-title.is-active {
      filter: blur(0);
      opacity: 1;
      transform: none;
    }

    .hero-title:not(.is-active) { pointer-events: none; }

    .hero-message-stage {
      display: grid;
      min-height: 6.5rem;
      width: 100%;
    }

    .hero-message {
      display: grid;
      gap: var(--space-4);
      grid-area: 1 / 1;
      transform-origin: 20% 50%;
    }

    h1, h2, p { margin: 0; }

    h1, .hero-stack-title {
      font-size: clamp(2.8rem, 6vw, 5.2rem);
      hyphens: auto;
      letter-spacing: 0;
      line-height: 0.95;
      overflow-wrap: break-word;
      text-wrap: balance;
    }

    .hero-copy p,
    .hero-stack-message {
      color: var(--color-text-secondary);
      font-size: clamp(1rem, 1.6vw, 1.2rem);
      line-height: 1.75;
      max-width: 34rem;
    }

    /* stacked (one column) presentation */
    .hero-stack {
      display: grid;
      gap: var(--space-12);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    .hero-stack-item {
      display: grid;
      gap: var(--space-3);
      justify-items: start;
    }

    /* appReveal fades the block in; headline, copy and panel then arrive in sequence so a stacked
       story reads the same way the wide hero does. */
    .hero-stack-item > * { opacity: 0; }

    .hero-stack-item.is-in > * {
      animation: fade-in-up var(--motion-medium) var(--ease-out) both;
    }

    .hero-stack-item.is-in .hero-stack-message { animation-delay: 90ms; }

    .hero-stack-item.is-in .hero-stack-panel {
      animation: hero-panel-in 720ms var(--ease-spring) both;
      animation-delay: 170ms;
    }

    .hero-stack-panel {
      margin-top: var(--space-5);
      width: 100%;
    }

    .hero-stack-panel--board { display: grid; justify-items: center; }

    /* align-items: center (not the grid default stretch): the training card is shorter than the
       live board, and stretching made it render at the board's height and then snap down to its own
       once the board left the DOM. Each panel now sizes to its own content from the first frame. */
    .hero-visual {
      align-items: center;
      display: grid;
      justify-items: center;
      min-height: 29rem;
      position: relative;
    }

    .hero-panel {
      align-self: center;
      grid-area: 1 / 1;
      transform-origin: 50% 60%;
      width: 100%;
    }

    .hero-panel--board { display: grid; justify-items: center; }

    .hero-message-enter {
      animation: hero-message-in 620ms var(--ease-spring) both;
      z-index: 2;
    }

    .hero-message-leave {
      animation: hero-message-out 320ms var(--ease-out) both;
      pointer-events: none;
      z-index: 1;
    }

    .hero-panel-enter {
      animation: hero-panel-in 720ms var(--ease-spring) both;
      z-index: 2;
    }

    .hero-panel-leave {
      animation: hero-panel-out 380ms var(--ease-out) both;
      pointer-events: none;
      z-index: 1;
    }

    @keyframes hero-message-in {
      from { filter: blur(8px); opacity: 0; transform: translateY(1.4rem) scale(0.985); }
      to { filter: blur(0); opacity: 1; transform: none; }
    }

    @keyframes hero-message-out {
      from { filter: blur(0); opacity: 1; transform: none; }
      to { filter: blur(6px); opacity: 0; transform: translateY(-0.8rem) scale(0.99); }
    }

    @keyframes hero-panel-in {
      from { filter: blur(8px); opacity: 0; transform: translateX(1.75rem) translateY(0.5rem) scale(0.965) rotate(1deg); }
      to { filter: blur(0); opacity: 1; transform: none; }
    }

    @keyframes hero-panel-out {
      from { filter: blur(0); opacity: 1; transform: none; }
      to { filter: blur(6px); opacity: 0; transform: translateX(-1.25rem) translateY(-0.25rem) scale(0.975) rotate(-0.6deg); }
    }

    /* soft brand glow staged behind the live board */
    .hero-visual::before {
      inset: -12% -10%;
      position: absolute;
      z-index: -1;
    }

    @media (min-width: 1024px) {
      .hero-visual {
        rotate: 1.2deg;
        transition: rotate var(--motion-slow) ease;
      }

      .hero-visual:hover {
        rotate: 0deg;
      }
    }

    @media (min-width: 901px) {
      .hero-scene { height: 80svh; }
      .hero {
        min-height: calc(80svh - 5.25rem);
        position: sticky;
        top: 4.25rem;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .hero-scene { height: auto; }
      .hero { min-height: 0; position: relative; top: auto; }
      .hero-title { filter: none; transition: none; transform: none; }
      .hero-title-stage,
      .hero-message-stage,
      .hero-visual { opacity: 1; }
      .hero.is-in .hero-title-stage,
      .hero.is-in .hero-message-stage,
      .hero.is-in .hero-visual { animation: none; }
      .hero-stack-item > * { opacity: 1; }
      .hero-stack-item.is-in > *,
      .hero-stack-item.is-in .hero-stack-panel { animation: none; }
      .hero-message-enter,
      .hero-message-leave,
      .hero-panel-enter,
      .hero-panel-leave { animation: none; }
    }
  `],
})
export class LandingHeroComponent implements OnDestroy {
  readonly demoTraining = DEMO_TRAINING;
  readonly activeSlide = signal(0);
  readonly stacked = signal(false);
  private readonly heroRef = viewChild<ElementRef<HTMLElement>>('hero');
  private readonly sceneRef = viewChild<ElementRef<HTMLElement>>('scene');
  private rotation: ReturnType<typeof setInterval> | undefined;
  private visibilityObserver?: IntersectionObserver;
  private stackQuery?: MediaQueryList;
  private heroVisible = true;
  private interactionPaused = false;
  private motionReduced = false;
  private scrollActivated = false;
  private scrollFrame = 0;
  private readonly onPresentationScroll = () => {
    if (this.scrollFrame) {
      return;
    }
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updatePresentationProgress();
    });
  };
  private readonly onStackQueryChange = (event: MediaQueryListEvent) => {
    this.stacked.set(event.matches);
    this.syncRotation();
  };

  constructor() {
    afterNextRender(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.motionReduced = true;
      }
      this.stackQuery = window.matchMedia(STACK_QUERY);
      this.stacked.set(this.stackQuery.matches);
      this.stackQuery.addEventListener('change', this.onStackQueryChange);
      window.addEventListener('scroll', this.onPresentationScroll, { passive: true });
      window.addEventListener('resize', this.onPresentationScroll, { passive: true });
      this.updatePresentationProgress();
      this.syncRotation();
    });

    // The rotating hero only exists in the wide branch, so the visibility observer is (re)attached
    // whenever that element comes back after a breakpoint switch.
    effect(() => {
      const hero = this.heroRef()?.nativeElement;
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = undefined;
      if (!hero) {
        return;
      }
      this.observeVisibility(hero);
    });
  }

  setInteractionPaused(paused: boolean): void {
    this.interactionPaused = paused;
    this.syncRotation();
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !this.heroRef()?.nativeElement.contains(next)) {
      this.setInteractionPaused(false);
    }
  }

  private startRotation(): void {
    if (this.rotation) {
      return;
    }
    this.rotation = setInterval(() => this.activeSlide.update((current) => (current + 1) % 2), 6500);
  }

  private syncRotation(): void {
    if (this.stacked() || !this.scrollActivated || this.motionReduced || this.interactionPaused || !this.heroVisible) {
      clearInterval(this.rotation);
      this.rotation = undefined;
      return;
    }
    this.startRotation();
  }

  private updatePresentationProgress(): void {
    const scene = this.sceneRef()?.nativeElement;
    const hero = this.heroRef()?.nativeElement;
    if (this.stacked() || !scene || !hero) {
      return;
    }

    const sceneRect = scene.getBoundingClientRect();
    const stickyTop = Number.parseFloat(getComputedStyle(hero).top) || 0;
    const travel = Math.max(1, scene.offsetHeight - (window.innerHeight - stickyTop));
    const progress = Math.min(1, Math.max(0, (stickyTop - sceneRect.top) / travel));
    const presentationComplete = progress >= 0.72;
    const guidedSlide = progress >= 0.36 ? 1 : 0;

    if (!presentationComplete && this.activeSlide() !== guidedSlide) {
      this.activeSlide.set(guidedSlide);
    } else if (presentationComplete && !this.scrollActivated) {
      this.activeSlide.set(1);
    }
    this.scrollActivated = presentationComplete;
    this.syncRotation();
  }

  private observeVisibility(hero: HTMLElement): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.heroVisible = entry?.isIntersecting ?? true;
      this.syncRotation();
    }, { threshold: 0.2 });
    this.visibilityObserver.observe(hero);
  }

  ngOnDestroy(): void {
    clearInterval(this.rotation);
    this.visibilityObserver?.disconnect();
    this.stackQuery?.removeEventListener('change', this.onStackQueryChange);
    window.removeEventListener('scroll', this.onPresentationScroll);
    window.removeEventListener('resize', this.onPresentationScroll);
    window.cancelAnimationFrame(this.scrollFrame);
  }
}
